import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import {
  Box,
  Button,
  FormHelperText,
  Grid,
  List,
  ListItemButton,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { isEmpty } from 'lodash-es';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Controller, FormProvider, useFieldArray, useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Cell } from 'react-table';
import { v4 as uuidv4 } from 'uuid';
import {
  ClientDTO,
  ClosureDTO,
  createClientInvoice,
  Field,
  FileDTO,
  getClosureByCandidateName,
  getCurrencyById,
  getInvoiceNumber,
  getTaxByTaxTypeList,
  IClientCurrencySettings,
  IClientInvoice,
  InvoiceExtra,
  ITaxTypesDetails,
  saveTaxType,
  ourEntityLegalIdentifierList,
  ourEntityBankList,
  FieldTypeEnum,
  updateClientInvoice,
  IOurEntity,
  getClientById,
  getAllVisibleTaxType,
  saveActivityLog,
  LogEntityType,
  LogAction,
  LogContextType,
  InvoiceItem,
  searchContractPlacements,
  InvoiceLineItem,
  getAllTermCondition,
  IOurEntityLegal,
} from 'wisestep-api';
import {
  ControlledTextInput,
  Label,
  WsButton,
  WsDataTable,
  WsDatePicker,
  WsDrawer,
  WsElementWithPopOver,
  WsIconAssignCandidate,
  WsIconText,
  WsSelect,
  WsSelectObj,
  WsTypoVariants,
} from 'wisestep-base';

import { ClientAutoCompleteFilter, ClientContactAutoComplete } from '../../../components';
import { SelectWCreate } from '../../../components/SelectWCreate';
import { WsAsyncAutocomplete } from '../../../components/WsAsyncAutocomplete';
import WsCheckBox from '../../../components/WsCheckBox/WsCheckBox';
import WsSwitch from '../../../components/WsSwitch';
import {
  getAvEditAddDesUiName,
  getEditFiledForPlacementToInvoiceConverstion,
  activityLogDTOHelper,
  getConversionUIName,
} from '../../../features/activityLog/ActivityHelper';
import { failed, success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { getFeeArrangementFees } from '../../clientDetails/components/contractArrangementsTab';
import CustomField from '../../clientListingPage/components/CustomField';
import { DeleteIconButton } from '../../crmSettings/components/StyledIconButtons';
import CreateOurEntityBankDialog from '../../ourEntity/ourentity/CreateOurEntityBankDialog';
import { ApplyTo, invoiceTaxSettingsDefaultPayload } from '../helper/InvocieSettingsHelper';

import AddEditInvoiceTypeDialog from './AddEditInvoiceTypeDialog';
import InvoiceAmountForm from './InvoiceAmountForm';
import {
  getContractPlacementSellRate,
  getContractPlacementUnits,
} from './invoiceDetails/InvoiceDetailContractPlacementDetails';
import InvoiceDrawerLayout from './InvoiceDrawerLayout';
import { getSubtotal, getSubtotalFromLineItems } from './SubTotal';

const getClosureLabel = (closure: ClosureDTO) =>
  `${closure.candidate?.name}-${closure.candidate?.email}`;

export const closureToCandidateField = (closure) => ({
  ...closure,
  label: `${closure.candidate.name}-${closure.candidate.email}`,
});

export interface IInvoiceForm {
  selectedOurEntityId: string;
  clientListDetailData: ClientDTO[];
  clientList: WsSelectObj[];
  invoiceTypes: WsSelectObj[];
  signatoryDetailList: WsSelectObj[];
  ourEntityLegalList: WsSelectObj[];
  bankDetailList: WsSelectObj[];
  selectedInvoiceTaxFields: ITaxTypesDetails[];
  setSelectedInvoiceTaxFields: Dispatch<SetStateAction<ITaxTypesDetails[]>>;
  selectedAdditionalTaxFields: ITaxTypesDetails[];
  setSelectedAdditionalTaxFields: Dispatch<SetStateAction<ITaxTypesDetails[]>>;
  downloadInvoice: (invoiceCode?: string) => void;
  currentInvoice?: IClientInvoice;
  invoiceCurrency?: IClientCurrencySettings;
  setInvoiceCurrency?: (invoiceCurrency?: IClientCurrencySettings) => void;
  setFormValues: (formValues) => void;
  setFormDataObjects: (formDataObjects) => void;
  saveInvoicePDF: (invoice: IClientInvoice) => Promise<FileDTO>;
  invoiceExtras: InvoiceExtra[];
  ourEntities: IOurEntity[];
  isConversion?: boolean;
  placementId?: string;
  defaultBankName?: string;
  ourEntityLegalIdentifier: IOurEntityLegal[];
  customFields?: Field[];
}

const InvoiceForm = ({
  selectedOurEntityId,
  clientListDetailData,
  invoiceTypes,
  bankDetailList,
  selectedInvoiceTaxFields,
  setSelectedInvoiceTaxFields,
  selectedAdditionalTaxFields,
  setSelectedAdditionalTaxFields,
  downloadInvoice,
  currentInvoice,
  invoiceCurrency,
  setInvoiceCurrency,
  setFormValues,
  setFormDataObjects,
  saveInvoicePDF,
  invoiceExtras,
  ourEntities,
  isConversion,
  placementId,
  defaultBankName,
  ourEntityLegalIdentifier,
  customFields,
}: IInvoiceForm) => {
  const methods = useFormContext();
  const {
    handleSubmit,
    setValue,
    watch,
    formState: { dirtyFields },
    getValues,
    control,
  } = methods;
  const clientId = watch('clientId');
  const clientName = watch('clientName');
  const [client, setClient] = useState();
  const clientInvoiceTypeId = watch('clientInvoiceTypeId');
  const raisedDate = watch('raisedDate');
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [invoiceTaxFields, setInvoiceTaxFields] = useState<ITaxTypesDetails[]>([]);
  const [invoiceTaxSuggestionData, setInvoiceTaxSuggestionData] = useState<string[]>([]);
  const [additionalTaxFields, setAdditionalTaxFields] = useState<ITaxTypesDetails[]>([]);
  const [additionalTaxSuggestionData, setAdditionalTaxSuggestionData] = useState<string[]>([]);
  const [legalId, setLegalId] = useState<string | null>(null);
  const [legalValues, setLegalValues] = useState<string[]>([]);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [openInvoiceTypeDialog, setOpenInvoiceTypeDialog] = useState<boolean>(false);
  const [openBankInfoDialog, setOpenBankInfoDialog] = useState<boolean>(false);
  const [openInvoiceTaxDialog, setOpenInvoiceTaxDialog] = useState<boolean>(false);
  const [openAdditionalTaxDialog, setOpenAdditionalTaxDialog] = useState<boolean>(false);
  const [isDraftValue, setIsDraftValue] = useState<boolean>(false);
  const [tempClientId, setTempClientId] = useState<string | null>(null);
  const navigate = useNavigate();
  const items = useFieldArray<{ items: InvoiceItem[] }, 'items', 'id'>({ name: 'items' });
  const lineItems = useFieldArray({ name: 'lineItems', keyName: 'rhfId' });
  const closures = watch('closures');
  const ourEntity = watch('ourEntityId');
  const values = watch();
  const [getDefaulTermConditionText, setShowTermConditionText] = useState<string>();

  useEffect(() => {
    (async () => {
      const { data: taxes } = await getAllVisibleTaxType();
      setInvoiceTaxFields(taxes.filter((t) => t.applyTo === 'Invoice'));
      setAdditionalTaxFields(taxes.filter((t) => t.applyTo === 'Taxes'));
      const getAllTermConditionReq = await getAllTermCondition();
      setShowTermConditionText(
        getAllTermConditionReq.data[0].isDefault
          ? getAllTermConditionReq.data[0].descriptionData.des
          : ''
      );
    })();
  }, [setShowTermConditionText]);

  useEffect(() => {
    if (currentInvoice?.invoiceStage === 'DRAFT') {
      setIsDraftValue(true);
    }
    (async () => {
      const response = await getTaxByTaxTypeList('Invoice');
      populateInvoiceTaxFields(response.data);
    })();

    (async () => {
      const response = await getTaxByTaxTypeList('Taxes');
      populateAdditionalTaxFields(response.data);
    })();
  }, [currentInvoice]);

  useEffect(() => {
    (async () => {
      if (clientId) {
        if (tempClientId == null) {
          setTempClientId(clientId);
          const response = await getClientById(clientId);
          setClient(response.data.data);
        } else if (clientId != tempClientId) {
          setTempClientId(clientId);
          methods.setValue('closures', []);
          const response = await getClientById(clientId);
          setClient(response.data.data);
        }
      }
    })();
  }, [clientId, methods, tempClientId]);
  useEffect(() => {
    (async () => {
      if (legalId == null) {
        setLegalId(ourEntity);
        const res = await ourEntityBankList(ourEntity, 0, 100);
        const objBid = res.data.data.filter((el) => el.isDefault === true);

        methods.setValue('ourEntityBankId', objBid.length > 0 ? objBid[0].id : '');
        const response = await ourEntityLegalIdentifierList(ourEntity, 0, 100);
        setLegalValues(response.data.data.map((legalDataId) => legalDataId.value));
        const object_id = response.data.data.filter((el) => el.isDefault === true);

        const valuesArray = object_id.map((obj) => obj.value);
        if (ourEntity) {
          methods.setValue('legalIdentifierValue', valuesArray);
        }
      } else if (ourEntity != legalId) {
        setLegalId(ourEntity);
        methods.setValue('legalIdentifierValue', []);
        const res = await ourEntityBankList(ourEntity, 0, 100);
        const objBid = res.data.data.filter((el) => el.isDefault === true);

        methods.setValue('ourEntityBankId', objBid.length > 0 ? objBid[0].id : '');
        const response = await ourEntityLegalIdentifierList(ourEntity, 0, 100);
        setLegalValues(response.data.data.map((legalDataId) => legalDataId.value));
        const object_id = response.data.data.filter((el) => el.isDefault === true);

        const valuesArray = object_id.map((obj) => obj.value);
        methods.setValue('legalIdentifierValue', valuesArray);
      }
    })();
  }, [ourEntity, legalId]);
  const columns =
    values.placementType === 'PERMANENT'
      ? [
          {
            Header: 'Description',
            accessor: 'closure.candidateName',
            Cell: (cell: Cell) => {
              const row = cell.row.original as InvoiceItem;
              return (
                (row.type === 'CLOSURE'
                  ? row.closure?.candidate?.name
                  : row.invoiceExtraCharge?.description) ?? null
              );
            },
          },
          {
            Header: 'Compensation Amount',
            Cell: (cell: Cell) => {
              const row = cell.row.original as InvoiceItem;
              const closure = cell.row.original['closure'];
              return row.type === 'CLOSURE'
                ? closure
                  ? `${closure.currency?.symbol}${closure.compensationAmount}`
                  : ''
                : null;
            },
          },
          {
            Header: 'Fee Arrangement',
            Cell: (cell: Cell) => {
              const row = cell.row.original as InvoiceItem;
              return row.type === 'CLOSURE'
                ? `${getFeeArrangementFees(cell.row.original['closure']?.feeArrangement)}`
                : null;
            },
          },
          {
            Header: 'Amount',
            Cell: (cell: Cell) => {
              const row = cell.row.original as InvoiceItem;
              const closure = cell.row.original['closure'];
              return row.type === 'CLOSURE'
                ? closure
                  ? `${closure.currency?.symbol}${closure.commissionValue}`
                  : ''
                : `${invoiceCurrency?.symbol} ${row.invoiceExtraCharge?.amount}`;
            },
          },
        ]
      : [
          {
            Header: 'Description',
            accessor: 'closure.candidateName',
            Cell: (cell: Cell) => {
              const row = cell.row.original as InvoiceLineItem;
              return (
                (row.type === 'CLOSURE'
                  ? row.contractPlacement?.candidate?.name
                  : row.description) ?? null
              );
            },
          },
          {
            Header: 'Rate',
            Cell: (cell: Cell) => {
              const row = cell.row.original as InvoiceLineItem;
              return row.type === 'CLOSURE'
                ? getContractPlacementSellRate(row.contractPlacement)
                : null;
            },
          },
          {
            Header: 'Units',
            Cell: (cell: Cell) => {
              const row = cell.row.original as InvoiceLineItem;
              return row.type === 'CLOSURE' ? getContractPlacementUnits(row) : null;
            },
          },
          {
            Header: 'Amount',
            Cell: (cell: Cell) => {
              const row = cell.row.original as InvoiceLineItem;
              return `${invoiceCurrency?.symbol} ${row.amount}`;
            },
          },
        ];

  const populateInvoiceTaxFields = (data) => {
    setInvoiceTaxSuggestionData(
      data.map((invoiceType) => `${invoiceType.name}-${invoiceType.rate}%`) ?? []
    );
  };

  const populateAdditionalTaxFields = (data) => {
    setAdditionalTaxSuggestionData(
      data.map((invoiceType) => `${invoiceType.name}-${invoiceType.rate}%`) ?? []
    );
  };

  const onSubmit = async (clientInvoice, isDraft) => {
    clientInvoice.extras = clientInvoice.extras.concat(
      clientInvoice.items
        .filter((item) => item.type === 'INVOICE_EXTRA')
        .map((item) => item.invoiceExtraCharge)
    );
    clientInvoice.closureIds = clientInvoice.items
      .filter((item) => item.type !== 'INVOICE_EXTRA')
      .map(({ closure: { id } }) => id);
    clientInvoice.items = (clientInvoice as IClientInvoice).items.map((item) => ({
      type: item.type,
      id: item.type === 'INVOICE_EXTRA' ? item.invoiceExtraCharge?.id : item.closure?.id,
    }));
    clientInvoice.currencyEntityId =
      invoiceCurrency?.id ?? clientInvoice.closures[0].closure.currencyId;
    clientInvoice.id = currentInvoice?.id;
    clientInvoice.invoiceStage = 'ISSUED';
    clientInvoice.invoiceStatus = 'ISSUED';

    if (isDraft === 'Draft') {
      clientInvoice.invoiceStage = 'DRAFT';
      clientInvoice.invoiceStatus = 'DRAFT';
    }
    clientInvoice.legalIdentifierIds = ourEntityLegalIdentifier.map((li) => li.legalIdentifierId);
    const avPayload = { ...activityLogDTOHelper };
    const avChangedValueViaDirtyFileds = Object.keys(dirtyFields).toString().split(',');
    const avEmptyEntityfields = Object.keys(
      Object.entries(currentInvoice ?? 0).reduce((acc, [k, v]) => {
        if (v === '' || v === null) acc[k] = v;
        return acc;
      }, {})
    );
    try {
      clientInvoice.pdfFile = await saveInvoicePDF(clientInvoice);
    } catch (e) {
      setNotification(failed('Failed to save Invoice PDF.'));
    }
    let savedInvoice;
    if (clientInvoice.id && !isEmpty(clientInvoice.id)) {
      const response = await updateClientInvoice(clientInvoice);
      savedInvoice = response.data;
      avPayload.action = LogAction.Edit;
      avPayload.description = `${getAvEditAddDesUiName(
        'Invoice',
        avEmptyEntityfields,
        avChangedValueViaDirtyFileds
      )} under Invoice`;
      avPayload.parentEntitiyType = LogContextType.Invoice;
      avPayload.parentEntitiyId = clientInvoice.id;
      avPayload.contextData = { clientId: clientInvoice.clientId };
    } else {
      const response = await createClientInvoice(clientInvoice);
      savedInvoice = response.data;
      if (response?.data) {
        clientInvoice = response.data;
        avPayload.action = LogAction.Add;

        if (isConversion) {
          const editFieldDes = getEditFiledForPlacementToInvoiceConverstion(
            currentInvoice?.clientId as string,
            currentInvoice?.ourEntityId as string,
            clientInvoice?.clientId as string,
            clientInvoice?.ourEntityId as string
          );
          avPayload.description = `${editFieldDes} ${getConversionUIName(
            'Invoice',
            avChangedValueViaDirtyFileds
          )} for Invoice ${response.data.invoiceCode} under Placement`;
          avPayload.parentEntitiyType = LogContextType.Placement;
          avPayload.parentEntitiyId = placementId as string;
          avPayload.contextData = { closureId: placementId };
        } else {
          avPayload.description = `ADDED ${clientInvoice.invoiceCode} Invoice under Client`;
          avPayload.parentEntitiyType = LogContextType.Client;
          avPayload.parentEntitiyId = clientInvoice.clientId;
          avPayload.contextData = { clientId: clientInvoice.clientId };
        }
      }
    }
    avPayload.entityType = LogEntityType.Invoices;
    avPayload.entityId = clientInvoice.id;
    avPayload.field = `Invoice Creation`;
    avPayload.entityName = clientInvoice.invoiceCode;
    avChangedValueViaDirtyFileds[0].length > 0 ? await saveActivityLog(avPayload) : '';
    downloadInvoice(clientInvoice.invoiceCode);
    setNotification(success('Invoice saved successfully.'));
    if (savedInvoice.invoiceStatus === 'DRAFT') {
      navigate(`/invoice/list`);
    } else {
      navigate(`/invoice/${clientInvoice.id}`);
    }
  };
  useEffect(() => {
    if (raisedDate && clientListDetailData && clientListDetailData.length > 0) {
      const foundClient = clientListDetailData?.filter((client) => client.id === clientId);
      if (foundClient) {
        const client: ClientDTO = foundClient[0];
        if (client && !isNaN(parseInt(client.paymentTerms))) {
          const result = new Date(raisedDate);
          result.setDate(result.getDate() + parseInt(client.paymentTerms));
        }
      }
    }
  }, [raisedDate, clientListDetailData, clientId, setValue]);

  useEffect(() => {
    setValue('subTotal', getSubtotal(getValues('items')));
  }, [closures, getValues, setValue]);

  useEffect(() => {
    if (!isEmpty(currentInvoice?.invoiceCode)) {
      return;
    }
    if (clientId && clientInvoiceTypeId) {
      const getInvoicenumberByType = async () => {
        const response = await getInvoiceNumber(clientInvoiceTypeId, clientId);
        setValue('invoiceCode', response.data.invoiceNumber);
      };
      getInvoicenumberByType();
    }
  }, [clientId, clientInvoiceTypeId, setValue, currentInvoice]);

  const onAddNewTaxType = async (data, taxCheckBoxList?) => {
    const payload = { ...invoiceTaxSettingsDefaultPayload };
    payload.name = data.fieldOne;
    payload.rate = data.fieldTwo;
    payload.isActive = true;
    payload.applyTo = ApplyTo.Invoice;
    if (taxCheckBoxList) {
      payload.applyTo = ApplyTo.Taxes;
      payload.applyToTaxes = data.applyToTaxes;

      if (data.applyToTaxes.length === 0 && taxCheckBoxList.length > 0) {
        setNotification(warning(`Please select at least one checkbox`));
        return;
      }
    }
    try {
      const response = await saveTaxType(payload);
      setNotification(
        success(
          `${data.fieldOne} has been added successfully! Please select the applicable taxation.`
        )
      );
      if (openInvoiceTaxDialog && response?.data) {
        invoiceTaxFields.push(response.data);
        populateInvoiceTaxFields(invoiceTaxFields);
        setOpenInvoiceTaxDialog(false);
      }
      if (openAdditionalTaxDialog && response?.data) {
        additionalTaxFields.push(response.data);
        populateAdditionalTaxFields(additionalTaxFields);
        setOpenAdditionalTaxDialog(false);
      }
    } catch (error: any) {
      if (error.response.status === 400)
        setNotification(warning(`Oops , ${data.fieldOne} already exists!!`));
      else setNotification(failed('Something went wrong !!'));
    }
  };
  const onCreateInvoiceType = async (data) => {
    setFormValues((prev) => ({
      ...prev,
      invoiceTypes: [
        ...prev.invoiceTypes,
        {
          label: data?.['name'],
          value: data?.id,
        },
      ],
    }));
    setFormDataObjects((prev) => ({
      ...prev,
      invoiceTypes: [...prev.invoiceTypes, data],
    }));
    setValue('clientInvoiceTypeId', data.id);
    const response = await getInvoiceNumber(data.id, clientId);
    setValue('invoiceCode', response.data.invoiceNumber);
    setOpenInvoiceTypeDialog(false);
  };

  const onCreateBankInfo = (data) => {
    setFormValues((prev) => ({
      ...prev,
      bankDetailList: [
        ...prev.bankDetailList,
        {
          label: data?.['bankName'],
          value: data?.id,
        },
      ],
    }));
    setFormDataObjects((prev) => ({
      ...prev,
      bankDetailsList: [...prev.bankDetailsList, data],
    }));
    setValue('ourEntityBankId', data.id);
    setOpenBankInfoDialog(false);
  };

  const addItem = (type) => {
    if (values.placementType === 'PERMANENT') {
      items.append({
        type,
        ...(type === 'INVOICE_EXTRA' && { invoiceExtraCharge: { id: uuidv4() } }),
      });
    } else {
      lineItems.append({ type });
    }
  };

  const AddInvoiceItemsButton = () => (
    <WsElementWithPopOver
      element={
        <WsButton variant="contained" size="medium" endIcon={<KeyboardArrowDownOutlinedIcon />}>
          Add Invoice Items
        </WsButton>
      }
    >
      <List dense={true} sx={{ pl: 1 }}>
        <ListItemButton
          onClick={() => {
            addItem('CLOSURE');
            setOpenDialog(true);
          }}
        >
          <WsIconText
            text="Add Candidate"
            icon={<WsIconAssignCandidate />}
            textCursor="pointer"
            textSize={WsTypoVariants.BODY1}
          />
        </ListItemButton>
        <ListItemButton
          onClick={() => {
            addItem('INVOICE_EXTRA');
            setOpenDialog(true);
          }}
        >
          <WsIconText
            text="Add Other Invoice Item"
            icon={<WsIconAssignCandidate />}
            textCursor="pointer"
            textSize={WsTypoVariants.BODY1}
          />
        </ListItemButton>
      </List>
    </WsElementWithPopOver>
  );

  const updateSubtotal = () => {
    if (values.placementType === 'PERMANENT')
      methods.setValue('subTotal', getSubtotal(getValues('items')));
    else {
      methods.setValue('subTotal', getSubtotalFromLineItems(getValues('lineItems')));
    }
  };

  const updateDueDate = () => {
    const closure = (
      getValues('placementType') === 'PERMANENT'
        ? items.fields.find((item) => item.type === 'CLOSURE')?.closure
        : lineItems.fields.find((item) => item['type'] === 'CLOSURE')?.['contractPlacement']
    ) as ClosureDTO | undefined;
    const paymentTerms = closure?.feeArrangement?.paymentTerms;
    if (raisedDate && paymentTerms) {
      const result = new Date(raisedDate);
      result.setDate(result.getDate() + +paymentTerms);
      setValue('dueDate', result);
    }
  };

  useEffect(() => {
    updateDueDate();
  }, [values.lineItems, values.items]);

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container>
            <Grid md={6} item p={2} sx={{ height: '4vh' }}>
              <ClientAutoCompleteFilter
                key={clientName}
                name="clientName"
                clientIdFieldName="clientName"
                selectedClientData={(data) => setValue('clientId', data)}
                isInvoice={true}
              />
            </Grid>
            <Grid md={6} item p={2}>
              <WsSelect
                name={'ourEntityId'}
                label={'Our Entity'}
                selectArray={ourEntities?.map((oe) => ({ label: oe.Name, value: oe.id! }))}
                defaultValue={currentInvoice?.ourEntityId ?? ''}
              />
            </Grid>
          </Grid>
          <Grid container>
            <Grid md={6} item p={2}>
              <SelectWCreate
                onCreate={() => setOpenInvoiceTypeDialog(true)}
                name={'clientInvoiceTypeId'}
                label={'Invoice Type'}
                defaultValue={currentInvoice?.clientInvoiceTypeId ?? ''}
              >
                {invoiceTypes.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </SelectWCreate>
            </Grid>
            <Grid md={6} item p={2}>
              <ControlledTextInput
                name="invoiceCode"
                type="text"
                fullWidth
                disabled={true}
                placeholder="Invoice Number"
                variant="outlined"
                labelText="Invoice Number"
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid md={6} item p={2}>
              <WsDatePicker name="raisedDate" label="Invoice Date" />
            </Grid>
            <Grid md={6} item p={2}>
              <WsDatePicker disabled name="dueDate" label="Due Date" />
            </Grid>
          </Grid>
          <Grid container mt={3}>
            <Grid md={6} item p={2}>
              {currentInvoice?.id ? (
                <SelectWCreate
                  onCreate={() => setOpenBankInfoDialog(true)}
                  name={'ourEntityBankId'}
                  label={'Bank Information'}
                  defaultValue={currentInvoice?.ourEntityBankId ?? ''}
                >
                  {bankDetailList.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </SelectWCreate>
              ) : (
                <SelectWCreate
                  onCreate={() => setOpenBankInfoDialog(true)}
                  name={'ourEntityBankId'}
                  label={'Bank Information'}
                  defaultValue={defaultBankName ?? ''}
                >
                  {bankDetailList.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </SelectWCreate>
              )}
            </Grid>
            <Grid md={6} item p={2}>
              <Grid item p={0.2} sm={12}>
                <CustomField
                  name="legalIdentifierValue"
                  label={'Legal Identifiers'}
                  description={''}
                  required={false}
                  type={FieldTypeEnum.Dropdown}
                  visible={true}
                  multiple={true}
                  options={legalValues}
                />
              </Grid>
            </Grid>
            {customFields?.map((field) => {
              const name = `customFields.${field.name}`;
              return (
                <Grid item key={field.id} p={2} sm={field.type === FieldTypeEnum.Checkbox ? 12 : 6}>
                  {field.type === FieldTypeEnum.ClientSPOC ? (
                    <>
                      <Label>{field.label}</Label>
                      <ClientContactAutoComplete
                        hideLabel
                        name="clientRecruiterOne"
                        clientId={clientId}
                        disabled={false}
                        clientContactIdFieldName={name}
                        placeholder="Search client contact"
                      />
                    </>
                  ) : (
                    <CustomField {...field} name={name} />
                  )}
                </Grid>
              );
            })}
          </Grid>
          <Stack px={2} mt={2} spacing={1} alignItems="center" direction="row">
            Invoice Items
            {(!currentInvoice?.id || isDraftValue) && (
              <>
                <Label style={{ marginLeft: 'auto' }}>Table </Label>
                <WsSwitch name="descriptive" />
                <Label> Description</Label>
                <AddInvoiceItemsButton />
              </>
            )}
          </Stack>
          {!currentInvoice?.id && (
            <Box px={2}>
              {values.placementType === 'PERMANENT' ? (
                <WsCheckBox name="showCompensationFeeArr" label="Compensation/Fee Arrangement" />
              ) : (
                <WsCheckBox name="showSellRateAndUnits" label="Sell Rate/Units" />
              )}
            </Box>
          )}
          <Grid md={12} item>
            <WsDataTable
              columns={columns}
              data={values.placementType === 'PERMANENT' ? values.items : values.lineItems}
              boldItems={['name']}
              pageable={false}
              totalDataItems={closures.length}
            />
          </Grid>
          <InvoiceAmountForm
            invoiceTaxFields={invoiceTaxFields}
            invoiceTaxSuggestionData={invoiceTaxSuggestionData}
            additionalTaxFields={additionalTaxFields}
            additionalTaxSuggestionData={additionalTaxSuggestionData}
            selectedInvoiceTaxFields={selectedInvoiceTaxFields}
            setSelectedInvoiceTaxFields={setSelectedInvoiceTaxFields}
            selectedAdditionalTaxFields={selectedAdditionalTaxFields}
            setSelectedAdditionalTaxFields={setSelectedAdditionalTaxFields}
            setOpenAdditionalTaxDialog={setOpenAdditionalTaxDialog}
            setOpenInvoiceTaxDialog={setOpenInvoiceTaxDialog}
            invoiceExtras={invoiceExtras}
            isDraft={isDraftValue}
            client={client}
            ourEntity={ourEntities.find((oe) => oe.id === ourEntity)}
            invoiceCurrency={invoiceCurrency}
          />
          <Grid container>
            <Grid md={12} item p={2}>
              <Stack direction="row" alignItems="center">
                <Label sx={{ mr: 2 }}>Terms and Conditions</Label>
              </Stack>
              <Controller
                name="termsAndConditions"
                control={control}
                render={({ field: { onChange, onBlur }, fieldState: { error } }) => (
                  <>
                    <TextField
                      onChange={(event) => {
                        onChange(event);
                        setShowTermConditionText(event.target.value);
                      }}
                      value={getDefaulTermConditionText}
                      onBlur={onBlur}
                      inputProps={{ style: { padding: '11.5px 14px' } }}
                      type="text"
                      fullWidth
                      multiline
                      maxRows={5}
                      placeholder="Terms and Conditions"
                      variant="outlined"
                    />
                    {error && <FormHelperText error>{error.message}</FormHelperText>}
                  </>
                )}
              />
            </Grid>
          </Grid>
          <Grid container>
            <Grid md={12} item p={2}>
              <ControlledTextInput
                name="description"
                type="text"
                fullWidth
                multiline
                maxRows={5}
                placeholder="Description/Comments"
                variant="outlined"
                labelText="Description/Comments"
              />
            </Grid>
          </Grid>

          <Grid container justifyContent="flex-end" mb={2}>
            <Grid item mr={2}>
              <Button
                id="start-button"
                variant="outlined"
                size="large"
                type="button"
                onClick={() => {
                  const clientInvoice = methods.getValues();
                  onSubmit(clientInvoice, 'Draft');
                }}
              >
                Save as Draft
              </Button>
            </Grid>
            <Grid item mr={2}>
              <Button id="start-button" variant="contained" size="large" type="submit">
                Save & Download
              </Button>
            </Grid>
          </Grid>
          <WsDrawer
            open={openDialog}
            title="Add Candidate"
            style={{ zIndex: '1300' }}
            onClose={() => setOpenDialog(false)}
          >
            <Grid container p={2} rowSpacing={4}>
              {values.placementType === 'PERMANENT'
                ? items.fields.map((field, index) => (
                    <Grid key={field.id} container item xs={12} spacing={1}>
                      {field.type !== 'INVOICE_EXTRA' ? (
                        <>
                          <Grid item sm={10.5}>
                            <WsAsyncAutocomplete
                              placeholder="Candidate Name"
                              name={`items.${index}.closure`}
                              changed={async (closure: ClosureDTO) => {
                                updateSubtotal();
                                if (isEmpty(invoiceCurrency) || closures.length == 1) {
                                  const response = await getCurrencyById(closure.currencyId ?? '');
                                  setInvoiceCurrency && setInvoiceCurrency(response.data);
                                } else if (
                                  invoiceCurrency &&
                                  invoiceCurrency.id != closure.currencyId
                                ) {
                                  setNotification(
                                    failed(
                                      'Candidate compensation currency mismatch. Invoices can be raised with single currency only!'
                                    )
                                  );
                                }
                                updateDueDate();
                              }}
                              getOptions={async (inputValue) => {
                                const { data } = await getClosureByCandidateName(
                                  methods.getValues().clientId,
                                  methods.getValues().ourEntityId,
                                  inputValue
                                );
                                return data.map(closureToCandidateField);
                              }}
                            />
                          </Grid>
                          <Grid item sm={3.5}>
                            <Label>Compensation Amount</Label>
                            <TextField
                              value={getValues(`items.${index}.closure`)?.compensationAmount}
                              fullWidth
                              disabled
                            />
                          </Grid>
                          <Grid item sm={3.5}>
                            <Label>Fee Arrangement</Label>
                            <TextField
                              value={getValues(`items.${index}.closure`)?.feeArrangement?.fee ?? ''}
                              fullWidth
                              disabled
                            />
                          </Grid>
                          <Grid item sm={3.5}>
                            <Label>Commission Value</Label>
                            <TextField
                              value={getValues(`items.${index}.closure`)?.commissionValue}
                              fullWidth
                              disabled
                            />
                          </Grid>
                        </>
                      ) : (
                        <>
                          <Grid item sm={10.5}>
                            <Label>Description</Label>
                            <ControlledTextInput
                              name={`items.${index}.invoiceExtraCharge.description`}
                              fullWidth
                              placeholder="Description"
                              variant="outlined"
                            />
                          </Grid>
                          <Grid item sm={5.25}>
                            <WsSelect
                              label="Invoice Extra"
                              name={`items.${index}.invoiceExtraCharge.invoiceExtra.id`}
                              selectArray={invoiceExtras
                                .filter((extra) => extra.behavior === 'DESCRIPTION')
                                .map(({ id, name }) => ({ label: name, value: id }))}
                            />
                          </Grid>
                          <Grid item sm={5.25}>
                            <Label>Amount</Label>
                            <ControlledTextInput
                              name={`items.${index}.invoiceExtraCharge.amount`}
                              fullWidth
                              placeholder="Amount"
                              variant="outlined"
                              size="small"
                              inputProps={{}}
                              changed={() => updateSubtotal()}
                            />
                          </Grid>
                        </>
                      )}
                      <Grid item p={1} sm={1} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <DeleteIconButton onClick={() => items.remove(index)} />
                      </Grid>
                    </Grid>
                  ))
                : lineItems.fields.map((field, index) => {
                    const lineItemFieldName = `lineItems.${index}`;
                    const closureFieldName = `${lineItemFieldName}.contractPlacement`;
                    return (
                      <Grid key={field.rhfId} container item xs={12} spacing={1}>
                        {field['type'] === 'CLOSURE' ? (
                          <>
                            <Grid item sm={10.5}>
                              <WsAsyncAutocomplete
                                placeholder="Candidate Name"
                                name={closureFieldName}
                                getOptions={async (inputValue) => {
                                  const { data } = await searchContractPlacements(
                                    methods.getValues().clientId,
                                    methods.getValues().ourEntityId,
                                    inputValue
                                  );
                                  return data.map(closureToCandidateField);
                                }}
                                getOptionLabel={getClosureLabel}
                                changed={async (closure: ClosureDTO) => {
                                  const closureCurrencyId = closure.sellRateCurrency?.id ?? '';
                                  updateSubtotal();
                                  if (isEmpty(invoiceCurrency) || closures.length == 1) {
                                    const response = await getCurrencyById(closureCurrencyId);
                                    setInvoiceCurrency && setInvoiceCurrency(response.data);
                                  } else if (
                                    invoiceCurrency &&
                                    invoiceCurrency.id != closureCurrencyId
                                  ) {
                                    setNotification(
                                      failed(
                                        'Candidate compensation currency mismatch. Invoices can be raised with single currency only!'
                                      )
                                    );
                                  }
                                  updateDueDate();
                                }}
                              />
                            </Grid>
                            <Grid item sm={3.5}>
                              <Label>Rate</Label>
                              <TextField
                                value={getContractPlacementSellRate(getValues(closureFieldName))}
                                fullWidth
                                disabled
                                inputProps={{ style: { padding: '11.5px 14px' } }}
                              />
                            </Grid>
                            <Grid item sm={3.5}>
                              <ControlledTextInput
                                labelText="Units"
                                name={`${lineItemFieldName}.units`}
                                changed={(v) => {
                                  setValue(
                                    `${lineItemFieldName}.amount`,
                                    +v * +getValues(closureFieldName)?.sellRateAmount
                                  );
                                  updateSubtotal();
                                }}
                                fullWidth
                              />
                            </Grid>
                            <Grid item sm={3.5}>
                              <ControlledTextInput
                                labelText="Invoice Value"
                                name={`${lineItemFieldName}.amount`}
                                disabled
                                fullWidth
                              />
                            </Grid>
                          </>
                        ) : (
                          <>
                            <Grid item sm={10.5}>
                              <Label>Description</Label>
                              <ControlledTextInput
                                name={`${lineItemFieldName}.description`}
                                fullWidth
                                placeholder="Description"
                                variant="outlined"
                              />
                            </Grid>
                            <Grid item sm={5.25}>
                              <WsSelect
                                label="Invoice Extra"
                                name={`${lineItemFieldName}.invoiceExtra.id`}
                                selectArray={invoiceExtras
                                  .filter((extra) => extra.behavior === 'DESCRIPTION')
                                  .map(({ id, name }) => ({ label: name, value: id }))}
                              />
                            </Grid>
                            <Grid item sm={5.25}>
                              <Label>Amount</Label>
                              <ControlledTextInput
                                name={`${lineItemFieldName}.amount`}
                                fullWidth
                                placeholder="Amount"
                                variant="outlined"
                                size="small"
                                inputProps={{}}
                                changed={() => updateSubtotal()}
                              />
                            </Grid>
                          </>
                        )}
                        <Grid item p={1} sm={1} sx={{ display: 'flex', justifyContent: 'center' }}>
                          <DeleteIconButton onClick={() => lineItems.remove(index)} />
                        </Grid>
                      </Grid>
                    );
                  })}
            </Grid>
            <Grid item p={1} m={1} sm={5.25}>
              <AddInvoiceItemsButton />
            </Grid>
            <Stack
              p={1}
              spacing={2}
              direction="row"
              sx={{ display: 'flex', justifyContent: 'right' }}
            >
              <WsButton variant="contained" size="medium" onClick={() => setOpenDialog(false)}>
                {`Continue`}
              </WsButton>
            </Stack>
          </WsDrawer>
        </form>
      </FormProvider>
      {openInvoiceTaxDialog && (
        <InvoiceDrawerLayout
          openDialog={openInvoiceTaxDialog}
          closeDialog={() => setOpenInvoiceTaxDialog(false)}
          onInput={(data) => onAddNewTaxType(data)}
          name="Add Invoice Tax"
          addPlaceholder1={'Tax Type'}
          addPlaceholder2={'Tax Rate'}
          isTaxTypeDrawer={true}
          checkBoxListData={[]}
        />
      )}
      {openAdditionalTaxDialog && (
        <InvoiceDrawerLayout
          openDialog={openAdditionalTaxDialog}
          closeDialog={() => setOpenAdditionalTaxDialog(false)}
          onInput={(data) => onAddNewTaxType(data, selectedInvoiceTaxFields)}
          name="Add Additional Tax"
          addPlaceholder1={'Tax Type'}
          addPlaceholder2={'Tax Rate'}
          isToggledEnabled={true}
          isTaxTypeDrawer={true}
          checkToggle={true}
          toggledText={'Tax applied on Invoice Taxation'}
          checkBoxListData={selectedInvoiceTaxFields}
        />
      )}
      {openInvoiceTypeDialog && (
        <AddEditInvoiceTypeDialog
          onCancel={() => {
            setValue('clientInvoiceTypeId', '');
            setOpenInvoiceTypeDialog(false);
          }}
          onSave={onCreateInvoiceType}
        />
      )}
      <CreateOurEntityBankDialog
        openDialog={openBankInfoDialog}
        ourEntityId={selectedOurEntityId}
        onCancel={() => {
          setValue('ourEntityBankId', '');
          setOpenBankInfoDialog(false);
        }}
        onSave={onCreateBankInfo}
      />
    </>
  );
};

export default InvoiceForm;
